import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Divider, InputBase, Stack, Typography } from "@mui/material";
import { useForm } from "../../../hooks";

import { PrincipalLayouts } from "../../../layouts";
import {
  fetchData,
  fetchDataValuationStartup,
} from "../../helpers/getData/fetchData";
import { DateInput } from "../DateInput";
import { ViewOpportuniteReservationTable } from "./ViewOpportuniteReservationTable";
import { createInvestments } from "../../helpers/investments";
import { toEnglishDate } from "../../../helpers/dateFormat";
import { useSelector } from "react-redux";
import { GetUsersWrapper } from "./GetUsersWrapper";

export const ViewOpportunity = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { t } = useTranslation("global");
  const [opportunityData, setOpportunityData] = useState("isLoading");
  const [isWaiting, setIsWaiting] = useState(false);
  const [hasInvestments, setHasInvestments] = useState("isLoading");
  const [vehiclesList, setVehicles] = useState("isLoading");
  const [reloadData, setReloadData] = useState(false);
  const date = new Date();
  const options = { day: "2-digit", month: "2-digit", year: "numeric" };
  const actualDate = date.toLocaleDateString("es-ES", options);
  const { dataToAction } = useSelector((state) => state.opportunity);

  const [formValues, handleInputChange] = useForm({
    investmentDate: toEnglishDate(actualDate),
    clubFilter: ["España"],
    isActiveFilter: "Activos",
    message: "",
  });

  const { investmentDate } = formValues;

  const opportunityParams = searchParams.get("opportunity");
  const startupParams = searchParams.get("startup");

  useEffect(() => {
    fetchData(
      setOpportunityData,
      `/reservation/reservationByOpportunity?opportunity=${opportunityParams}`,
      t
    );
    fetchData(
      setHasInvestments,
      `/investments/hasInvestment?opportunity=${opportunityParams}`,
      t
    );
    fetchData(setVehicles, "/vehicle/nameList", t);
    opportunityData.length === 0 && setIsWaiting(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, reloadData, opportunityData.length]);

  const handleCancel = () => {
    navigate("/opportunities");
  };

  const handleConfirm = async () => {
    setIsWaiting(true);
    let initialValuation = -1;
    const formData = opportunityData.filter(
      (reservation) => reservation.intention === "Reserva realizada"
    );
    let formDataWithInvestmentDate = formData.map((reservation) => {
      const { id, ...rest } = reservation;
      return { ...rest, investmentDate };
    });

    if (dataToAction.investmentType === "Ampliación Capital") {
      initialValuation = await fetchDataValuationStartup(
        `/startupValoration/byDate?date=${investmentDate}&startup=${startupParams}`,
        t
      );

      formDataWithInvestmentDate = formData.map((reservation) => {
        const { id, ...rest } = reservation;
        return {
          ...rest,
          investmentDate,
          participationValuation: initialValuation,
        };
      });
    }
    const created = initialValuation
      ? await createInvestments(
          formDataWithInvestmentDate,
          t("investments.successCreateMsg"),
          t("investments.errorCreateMsg")
        )
      : false;
    setIsWaiting(false);
    created && navigate("/opportunities");
  };

  return (
    <PrincipalLayouts>
      {opportunityData !== "isLoading" && vehiclesList !== "isLoading" && (
        <ViewOpportuniteReservationTable
          rows={opportunityData.map((opportunity) => {
            return {...opportunity, idCrm: opportunity.individual.idCrm, club: opportunity.individual.club}
          })}
          setReloadData={setReloadData}
          vehiclesList={vehiclesList}
        />
      )}
      {hasInvestments !== "isLoading" && hasInvestments?.investmentDate && (
        <Stack textAlign="center" sx={{ marginTop: "60px" }}>
          <Typography color="secondary" variant="h5">
            {t("investments.investmentMade")}
          </Typography>
        </Stack>
      )}
      {hasInvestments !== "isLoading" && !hasInvestments?.investmentDate && (
        <Stack direction="column" width="100%" gap={2} marginTop={4}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography color="primary" variant="h6">
              {t("investments.addTitle")}
            </Typography>
          </Stack>
          <Divider sx={{ marginTop: 2, marginBottom: 3 }} />
          <DateInput
            name={investmentDate}
            title="investmentDate"
            handleInputChange={handleInputChange}
            required={true}
          />
          <Stack direction="row" justifyContent="end" gap={2} marginTop={3}>
            <Button
              sx={{ width: "140px" }}
              color="secondary"
              onClick={handleCancel}
              variant="outlined"
            >
              {t("myprofile.cancelButton")}
            </Button>
            <Button
              disabled={isWaiting}
              onClick={handleConfirm}
              sx={{ width: "140px" }}
              type="submit"
              variant="contained"
            >
              {t("myprofile.confirmButton")}
            </Button>
          </Stack>
        </Stack>
      )}
      <Stack gap={2} mt={2}>
        <Stack>
          <Typography color="primary" variant="h6">
            {t("addevent.comments")}
          </Typography>
          <Divider sx={{ marginTop: 2, marginBottom: 1 }} />
        </Stack>

        {opportunityData !== "isLoading" &&
          opportunityData
            .filter((res) => res.comments !== "")
            .map((reservation, index) => {
              return (
                <Stack key={index}>
                  <Typography variant="overline" color="secondary">
                    {reservation.whoInvests}
                  </Typography>
                  <InputBase
                    fullWidth
                    multiline
                    size="small"
                    variant="standard"
                    value={reservation.comments}
                  />
                </Stack>
              );
            })}
      </Stack>
      {opportunityData !== "isLoading" && (
        <GetUsersWrapper
          formValues={formValues}
          handleInputChange={handleInputChange}
          startupId={startupParams}
          userWhithReservation={opportunityData?.map(
            (reservation) => reservation.individual.idCrm
          )}
        />
      )}
    </PrincipalLayouts>
  );
};
